import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import Table from '../../../../../components/Table';
import rbacCheck from '../../../../../rbac/rbacCheck';
import { fetchProductTypes } from '../../../../../redux/safescarf/productTypes/actions';

import CreateProductTypeFormModal from './CreateProductTypeFormModal';

import { useUserData } from '@/redux/selectors';
import type { RootState } from '@/redux/store';
import type { SafeScarfProduct } from '@/types/safeScarf';
import type { TableField, TableRowData } from '@/types/tableTypes';

const ProductTypes: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useUserData();
  const productTypes = useSelector(
    (state: RootState) => state.safescarf.productTypes
  ) as SafeScarfProduct[];

  const [createModalOpened, setCreateModalOpened] = useState<boolean>(false);

  const canCreateProductType = rbacCheck(userData.roles, '/products/safescarf/product-types:POST');

  useEffect(() => {
    if (!productTypes.length) dispatch(fetchProductTypes());
  }, [productTypes.length, dispatch]);

  const fields: TableField[] = [
    {
      id: 'name',
      title: 'Name',
    },
    {
      id: 'description',
      title: 'Description',
    },
  ];

  const rows = productTypes.map(productType => ({
    key: productType.id,
    onRowClick: () => navigate(productType.id),
    data: [
      {
        field: 'name',
        value: productType.name,
      },
      {
        field: 'description',
        value: productType.description,
      },
    ],
  })) as TableRowData[];

  return (
    <>
      {createModalOpened && (
        <CreateProductTypeFormModal
          onCancel={() => {
            setCreateModalOpened(false);
          }}
          onSubmit={() => {
            setCreateModalOpened(false);
          }}
        />
      )}
      <Table
        header='Product Types'
        fields={fields}
        rows={rows}
        createAction={{
          disabled: !canCreateProductType,
          onClick: () => {
            setCreateModalOpened(true);
          },
          tooltip: !canCreateProductType
            ? intl.formatMessage({ id: 'createProductTypeDisabled' })
            : '',
        }}
      />
    </>
  );
};

export default ProductTypes;
